import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Dialog from "../../../components/ui/Dialog";
import Pagination from "../../../components/ui/Pagination";
import BranchSelect from "../../../components/ui/BranchSelect";
import {
  useReviewSession,
  useSessionsForReview,
} from "../../../api/queries/attendanceQueries";
import { getApiErrorMessage, getApiErrorStatus } from "../../../utils/apiError";
import { formatPlainDate, formatTime } from "../../../utils/dateTime";
import LocationsModal from "../../attendance/components/LocationsModal";
import {
  SOURCE_LABEL,
  formatDistance,
} from "../../attendance/components/attendanceFormat";
import { useCanModify } from "../../../hooks/usePermissions";

const EMPTY = [];

/** Approve (remarks optional) or reject (reason required) one check-in period. */
const ReviewDialog = ({ session, action, onClose }) => {
  const isReject = action === "REJECT";
  const [remarks, setRemarks] = useState("");
  const review = useReviewSession();

  const submit = () =>
    review.mutate(
      { id: session.id, action, remarks: remarks.trim() || undefined },
      {
        onSuccess: () => {
          toast.success(
            isReject
              ? "Check-in rejected: its time won't be counted."
              : "Check-in approved.",
          );
          onClose();
        },
        onError: (err) => {
          toast[getApiErrorStatus(err) === 409 ? "info" : "error"](
            getApiErrorMessage(err, "Could not review this check-in."),
          );
          onClose();
        },
      },
    );

  return (
    <Dialog
      title={isReject ? "Reject check-in?" : "Approve check-in?"}
      subtitle={`${session.employee?.name || ""} · ${formatPlainDate(session.date)} · ${formatTime(session.checkInTime)}${session.checkOutTime ? `–${formatTime(session.checkOutTime)}` : ""}`}
      onClose={onClose}
      busy={review.isPending}
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onClose}
            disabled={review.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`btn profile-btn ${isReject ? "btn-danger" : "btn-success"}`}
            onClick={submit}
            disabled={review.isPending || (isReject && !remarks.trim())}
          >
            {review.isPending && (
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
            )}
            {isReject ? "Reject" : "Approve"}
          </button>
        </>
      }
    >
      {session.statusReason && (
        <div className="alert alert-warning fs-085 py-2">
          {session.statusReason}
        </div>
      )}
      {isReject && (
        <p className="fs-080 text-slate-600">
          Rejected time is not counted in the employee's hours.
        </p>
      )}
      <label htmlFor="validation-remarks" className="profile-label">
        {isReject ? (
          <>
            Reason <span className="text-danger">*</span>
          </>
        ) : (
          "Remarks (optional)"
        )}
      </label>
      <textarea
        id="validation-remarks"
        className="form-control"
        rows={3}
        maxLength={500}
        value={remarks}
        onChange={(e) => setRemarks(e.target.value)}
        placeholder={
          isReject
            ? "e.g. Not on an approved field visit"
            : "e.g. Client visit confirmed"
        }
        autoFocus
      />
    </Dialog>
  );
};

/**
 * Check-in periods waiting for my validation (outside the branch radius,
 * etc.). Replaces correction requests. Never includes my own.
 */
const ValidationQueue = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [branchId, setBranchId] = useState("");
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [locationsFor, setLocationsFor] = useState(null);
  const [reviewing, setReviewing] = useState(null); // { session, action }
  const canModify = useCanModify("attendance"); // view-only roles: no approve / reject

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchText.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchText]);

  const query = useSessionsForReview({ branchId, search, page, limit });
  const rows = query.data?.data || EMPTY;

  return (
    <>
      <div className="approval-filters mb-3">
        <div className="position-relative approval-search">
          <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-slate-500 fs-080"></i>
          <input
            type="search"
            className="filter-input w-100 ps-5"
            placeholder="Search name or employee number"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            aria-label="Search employees"
          />
        </div>
        <div className="att-filter-branch">
          <BranchSelect
            value={branchId}
            onChange={(v) => {
              setBranchId(v || "");
              setPage(1);
            }}
            id="validation-branch"
          />
        </div>
        {query.isFetching && !query.isLoading && (
          <span
            className="spinner-border spinner-border-sm text-primary"
            role="status"
            aria-label="Refreshing"
          ></span>
        )}
      </div>

      <div className="glass-table-card">
        {query.isError ? (
          <div className="approval-empty text-danger">
            <i className="bi bi-exclamation-triangle"></i>
            {getApiErrorMessage(query.error, "Could not load the check-ins.")}
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-hover mb-0 approval-table">
                <thead className="custom-table-header">
                  <tr>
                    <th className="ps-4 py-3">Employee</th>
                    <th className="py-3">Date</th>
                    <th className="py-3">Check-in · out</th>
                    <th className="py-3">Why it needs validation</th>
                    <th className="py-3">Source</th>
                    <th className="py-3 pe-4 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody className="border-top-0">
                  {rows.map((s) => (
                    <tr key={s.id}>
                      <td className="ps-4">
                        <div className="emp-name-text">
                          {s.employee?.name || "—"}
                        </div>
                        <div className="emp-sub-text">
                          {s.employee?.employeeNumber}
                          {s.employee?.branchName
                            ? ` · ${s.employee.branchName}`
                            : ""}
                        </div>
                      </td>
                      <td className="text-nowrap">
                        {formatPlainDate(s.date, "ddd, MMM D")}
                      </td>
                      <td className="text-nowrap">
                        {formatTime(s.checkInTime)} ·{" "}
                        {s.checkOutTime ? (
                          formatTime(s.checkOutTime)
                        ) : (
                          <span className="text-warning-emphasis">open</span>
                        )}
                      </td>
                      <td>
                        <div className="fs-080">{s.statusReason || "—"}</div>
                        {s.checkIn?.distance !== null &&
                          s.checkIn?.distance !== undefined && (
                            <div className="fs-070 text-slate-500">
                              In: {formatDistance(s.checkIn.distance)}
                              {s.checkOut?.distance !== null &&
                              s.checkOut?.distance !== undefined
                                ? ` · Out: ${formatDistance(s.checkOut.distance)}`
                                : ""}
                            </div>
                          )}
                        {s.remarks && (
                          <div className="fs-070 text-slate-600">
                            <i className="bi bi-chat-left-text me-1"></i>
                            {s.remarks}
                          </div>
                        )}
                      </td>
                      <td className="fs-075">
                        {SOURCE_LABEL[s.source] || s.source || "—"}
                      </td>
                      <td className="pe-4 text-end text-nowrap">
                        <div className="d-inline-flex gap-1">
                          <button
                            type="button"
                            className="btn btn-sm btn-light border att-action-btn"
                            onClick={() => setLocationsFor(s.attendanceId)}
                          >
                            <i className="bi bi-geo-alt"></i> Locations
                          </button>
                          {canModify && (
                            <>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger att-action-btn"
                                onClick={() =>
                                  setReviewing({ session: s, action: "REJECT" })
                                }
                              >
                                Reject
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-success att-action-btn"
                                onClick={() =>
                                  setReviewing({
                                    session: s,
                                    action: "APPROVE",
                                  })
                                }
                              >
                                Approve
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!query.isLoading && rows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="approval-empty">
                        <i className="bi bi-patch-check"></i>
                        No check-ins waiting for your validation.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {query.isLoading && (
              <div className="text-center py-5">
                <span
                  className="spinner-border text-primary"
                  role="status"
                  aria-label="Loading"
                ></span>
              </div>
            )}
            <Pagination
              page={page}
              limit={limit}
              total={query.data?.total || 0}
              onPageChange={setPage}
              onLimitChange={(l) => {
                setLimit(l);
                setPage(1);
              }}
            />
          </>
        )}
      </div>

      {locationsFor && (
        <LocationsModal
          attendanceId={locationsFor}
          onClose={() => setLocationsFor(null)}
        />
      )}
      {reviewing && (
        <ReviewDialog
          session={reviewing.session}
          action={reviewing.action}
          onClose={() => setReviewing(null)}
        />
      )}
    </>
  );
};

export default ValidationQueue;
